<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import FlashIcon from "@/components/ui/FlashIcon.vue";
import type { InfoLevel } from "@/model/baseTypes";
import { useLinkStore } from "@/store/link";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { useUrlStore } from "@/store/url";

const { t } = useI18n();

const active = computed(() => !!useUrlStore().search.link);
const show = computed(() => useLinkStore().hasRiskyLinks);
const linkTypeCount = computed(() => useLinkStore().riskyLinkCount);
const riskyLinkTooltipText = computed(() =>
  linkTypeCount.value.critical > 0 && linkTypeCount.value.risky === 0
    ? t("linkState.criticalLinks")
    : t("linkState.riskyLinks"),
);

function showRiskyLinks() {
  useSearchMenuStore().selectedLinkStates = new Set([
    ...(linkTypeCount.value.critical ? ["error" as InfoLevel] : []),
    ...(linkTypeCount.value.risky ? ["warn" as InfoLevel] : []),
  ]);
  useSearchMenuStore().expandSearch();
}
</script>

<template>
  <BaseTooltip position="bottom">
    <BaseButton
      v-show="show"
      id="risky-links"
      data-no-screenshot
      variant="ghost"
      :activated="active"
      :aria-label="riskyLinkTooltipText"
      @click="showRiskyLinks"
    >
      <div class="risky-links-icons">
        <FlashIcon
          v-if="linkTypeCount.critical"
          link-state="error"
          data-testid="critical-link-icon"
        />
        <FlashIcon v-if="linkTypeCount.risky" link-state="warn" data-testid="risky-link-icon" />
      </div>
    </BaseButton>
    <template #content>
      {{ riskyLinkTooltipText }}
    </template>
  </BaseTooltip>
</template>

<style lang="scss">
@use "@/styles/variables";

#risky-links {
  padding: 8px;

  .risky-links-icons {
    display: flex;
    gap: 4px;
  }
}
</style>
